/**
 * House of Dusk · fiche · carrousel de videos clients facon IM8 (blocks/_hod-pdp-videos.liquid).
 * Releve IM8 et compte rendu : 00_AI_BRAIN/06_RELEVE_THEME/lots/videos_pdp.md.
 *
 * <hod-pdp-videos> :
 * - rail : defilement natif (scroll-snap de .hod-rail) ; a la souris, glisser fait defiler (seuil
 *   10 px comme le Swiper d'IM8) et le clic qui suit un glisser n'ouvre rien ;
 * - clic sur une miniature : une seule <video> (preload none) recoit la source de la carte (HLS si le
 *   navigateur le lit nativement, sinon mp4), la feuille <hod-sheet> du theme s'ouvre (showModal,
 *   piege de focus, Echap, voile, verrou html[scroll-lock] d'Horizon, focus rendu a la miniature)
 *   et la video part avec le son ; refus du navigateur : elle repart muette, le bouton son le dit ;
 * - popup : toucher la video = pause ou reprise, bouton son, precedent / suivant en boucle (boutons,
 *   fleches du clavier, balayage au doigt), annonce « Video n of N » pour les lecteurs d'ecran ;
 * - fermeture (croix, voile, Echap, feuille retiree) : pause et retour a 0.
 * Aucune librairie, aucune lecture automatique hors du clic.
 */
const parse = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const DRAG_THRESHOLD = 10;
const SWIPE_MIN = 40;

class HodPdpVideos extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #index = 0;
  #muted = false;
  #dragged = false;
  #swiped = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;

    this.labels = parse(this.dataset.labels || '', {}) || {};
    this.#muted = this.hasAttribute('data-start-muted');
    this.rail = this.querySelector('[data-hod-videos-rail]');
    this.sheet = this.querySelector('hod-sheet');
    this.dialog = this.sheet ? this.sheet.querySelector('dialog') : null;
    this.stage = this.querySelector('[data-hod-video-stage]');
    this.video = /** @type {HTMLVideoElement | null} */ (this.querySelector('[data-hod-video]'));
    this.status = this.querySelector('[data-hod-video-status]');
    if (!this.rail || !this.dialog || !this.video || !this.stage) return;

    this.rail.addEventListener('click', this.#onCardClick, { signal });
    this.rail.addEventListener('pointerdown', this.#onRailDown, { signal });
    this.rail.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    this.dialog.addEventListener('click', this.#onDialogClick, { signal });
    this.dialog.addEventListener('keydown', this.#onDialogKey, { signal });
    this.dialog.addEventListener('close', () => this.#stop(), { signal });
    this.sheet.addEventListener('hod:sheet:close', () => this.#stop(), { signal });
    this.stage.addEventListener('pointerdown', this.#onStageDown, { signal });

    const sync = () => this.#syncState();
    this.video.addEventListener('play', sync, { signal });
    this.video.addEventListener('pause', sync, { signal });
    this.video.addEventListener('ended', sync, { signal });
    this.video.addEventListener('volumechange', () => this.#syncSound(), { signal });
    this.#syncSound();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#stop();
  }

  get cards() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-hod-video-open]')]);
  }

  /* ----------------------------------------------------------------------- */
  /* Ouverture, chargement, lecture                                          */
  /* ----------------------------------------------------------------------- */

  /** @param {number} index @param {HTMLElement | null} trigger */
  open(index, trigger = null) {
    this.#load(index);
    const sheet = /** @type {any} */ (this.sheet);
    if (sheet && typeof sheet.open === 'function') {
      sheet.open(trigger);
    } else if (this.dialog && !this.dialog.open) {
      this.dialog.showModal();
    }
    if (this.dialog && this.dialog.open) this.#play();
  }

  /** @param {number} step */
  step(step) {
    const total = this.cards.length;
    if (total < 2) return;
    this.#load(this.#index + step);
    this.#play();
  }

  /** @param {number} index */
  #load(index) {
    const cards = this.cards;
    const total = cards.length;
    if (!total || !this.video) return;
    this.#index = ((index % total) + total) % total;
    const card = cards[this.#index];
    const video = this.video;
    const hls = card.dataset.hls || '';
    const mp4 = card.dataset.src || '';
    const nativeHls = hls !== '' && video.canPlayType('application/vnd.apple.mpegurl') !== '';
    const src = nativeHls ? hls : mp4 || '';

    if ((video.dataset.current || '') !== src) {
      video.pause();
      if (src) {
        video.poster = card.dataset.poster || '';
        video.src = src;
      } else {
        video.removeAttribute('src');
        video.removeAttribute('poster');
        video.load();
      }
      video.dataset.current = src;
    } else {
      this.#rewind();
    }
    this.stage?.toggleAttribute('data-empty', !src);

    if (this.status) {
      const template = this.labels.status || 'Video [n] of [total]';
      this.status.textContent = template.replace('[n]', String(this.#index + 1)).replace('[total]', String(total));
    }
    this.#syncState();
  }

  #play() {
    const video = this.video;
    if (!video || !video.getAttribute('src')) {
      this.#syncState();
      return;
    }
    video.muted = this.#muted;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch((error) => {
        // Lecture avec le son refusee sans geste reconnu : on repart muet, le bouton son le montre.
        if (error && error.name === 'NotAllowedError' && !video.muted) {
          this.#muted = true;
          video.muted = true;
          video.play().catch(() => this.#syncState());
        } else {
          this.#syncState();
        }
      });
    }
    this.#syncSound();
  }

  #rewind() {
    const video = this.video;
    if (!video) return;
    try {
      if (video.currentTime) video.currentTime = 0;
    } catch {
      /* metadonnees pas encore chargees : rien a remettre a 0 */
    }
  }

  #stop() {
    const video = this.video;
    if (!video) return;
    video.pause();
    this.#rewind();
  }

  /* ----------------------------------------------------------------------- */
  /* Etats visibles                                                          */
  /* ----------------------------------------------------------------------- */

  #syncState() {
    const video = this.video;
    if (!video || !this.stage) return;
    const playing = !video.paused && !video.ended && Boolean(video.getAttribute('src'));
    this.stage.dataset.state = playing ? 'playing' : 'paused';
    const toggle = this.querySelector('[data-hod-video-toggle]');
    if (toggle) {
      const label = playing ? this.labels.pause : this.labels.play;
      if (label) toggle.setAttribute('aria-label', label);
    }
  }

  #syncSound() {
    const button = this.querySelector('[data-hod-video-sound]');
    if (!button || !this.video) return;
    const muted = this.video.getAttribute('src') ? this.video.muted : this.#muted;
    button.querySelector('.hod-pdp-videos__ico-sound')?.toggleAttribute('hidden', muted);
    button.querySelector('.hod-pdp-videos__ico-muted')?.toggleAttribute('hidden', !muted);
    const label = muted ? this.labels.soundOn : this.labels.soundOff;
    if (label) button.setAttribute('aria-label', label);
  }

  /* ----------------------------------------------------------------------- */
  /* Ecoutes                                                                 */
  /* ----------------------------------------------------------------------- */

  #onCardClick = (/** @type {MouseEvent} */ event) => {
    const card = event.target instanceof Element ? event.target.closest('[data-hod-video-open]') : null;
    if (!(card instanceof HTMLElement)) return;
    event.preventDefault();
    if (this.#dragged) {
      this.#dragged = false;
      return;
    }
    this.open(this.cards.indexOf(card), card);
  };

  #onDialogClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-hod-video-toggle]')) {
      if (this.#swiped) {
        this.#swiped = false;
        return;
      }
      const video = this.video;
      if (!video || !video.getAttribute('src')) return;
      if (video.paused) this.#play();
      else video.pause();
      return;
    }
    if (target.closest('[data-hod-video-sound]')) {
      this.#muted = !(this.video && this.video.getAttribute('src') ? this.video.muted : this.#muted);
      if (this.video) this.video.muted = this.#muted;
      this.#syncSound();
      return;
    }
    const step = target.closest('[data-hod-video-step]');
    if (step instanceof HTMLElement) this.step(Number(step.dataset.hodVideoStep) || 1);
  };

  #onDialogKey = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    // barre du navigateur : les fleches y avancent dans la video
    if (event.target === this.video && this.video?.controls) return;
    event.preventDefault();
    this.step(event.key === 'ArrowLeft' ? -1 : 1);
  };

  /** Balayage au doigt sur la video : suivante vers la gauche, precedente vers la droite. */
  #onStageDown = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse' || !event.isPrimary) return;
    this.#swiped = false;
    const x0 = event.clientX;
    const y0 = event.clientY;
    const stage = this.stage;
    if (!stage) return;
    const up = (/** @type {PointerEvent} */ end) => {
      stage.removeEventListener('pointerup', up);
      stage.removeEventListener('pointercancel', cancel);
      const dx = end.clientX - x0;
      const dy = end.clientY - y0;
      if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.#swiped = true;
        setTimeout(() => (this.#swiped = false), 400);
        this.step(dx < 0 ? 1 : -1);
      }
    };
    const cancel = () => {
      stage.removeEventListener('pointerup', up);
      stage.removeEventListener('pointercancel', cancel);
    };
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', cancel);
  };

  /** Glisser a la souris sur le rail (le doigt et le pave tactile defilent nativement). */
  #onRailDown = (/** @type {PointerEvent} */ event) => {
    const rail = this.rail;
    if (!rail || event.pointerType !== 'mouse' || event.button !== 0) return;
    if (rail.scrollWidth <= rail.clientWidth + 1) return;
    this.#dragged = false;
    const x0 = event.clientX;
    const left0 = rail.scrollLeft;
    let dragging = false;
    const move = (/** @type {PointerEvent} */ e) => {
      const dx = e.clientX - x0;
      if (!dragging && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!dragging) {
        dragging = true;
        rail.classList.add('is-dragging');
      }
      e.preventDefault();
      rail.scrollLeft = left0 - dx;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (!dragging) return;
      this.#dragged = true;
      // le clic arrive juste apres pointerup ; sans clic (relache hors carte), on libere le drapeau
      setTimeout(() => (this.#dragged = false), 0);
      const left = rail.scrollLeft;
      rail.classList.remove('is-dragging');
      // retour du scroll-snap : la carte la plus proche se cale, sans saut depuis la position lachee
      rail.scrollLeft = left;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
}

if (!customElements.get('hod-pdp-videos')) customElements.define('hod-pdp-videos', HodPdpVideos);
